import { Page } from "../../../common/components/Page";
import { PartyInviteList } from "../components/PartyInviteList";
import { useMyPartyInvites } from "../hooks/useMyPartyInvites";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@bw/shared/hooks";
import type { PartyInviteResponse } from "@bw/shared/types/generated";
import { useRespondPartyInvite } from "../hooks/useRespondPartyInvite";
import { mapRespondPartyInviteError } from "../lib/mapRespondPartyInviteError";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../common/query/queryClient";
import { visuallyHidden } from "../../../common/visuallyHidden";

/**
 * Party invites inbox (/me/invitations?type=open_play).
 *
 * Acceptance stays in the admission-aware facility session hub. Decline uses
 * the same owner command here without requiring a new check-in or payment.
 */
export function PartyInvitesInboxPage() {
  const { user } = useAuth();
  if (!user) return null;
  return <PartyInvitesInboxBody key={user.uid} actorId={user.uid} />;
}

function PartyInvitesInboxBody({ actorId }: { actorId: string }) {
  const queryClient = useQueryClient();
  const invitesQuery = useMyPartyInvites();
  const respond = useRespondPartyInvite();
  const invites = invitesQuery.data ?? [];
  const pending = useRef(new Set<string>());
  const mounted = useRef(true);
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const [declineError, setDeclineError] = useState<string | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const sourceIsCurrent = invitesQuery.isSuccess && invitesQuery.isFetchedAfterMount &&
    invitesQuery.fetchStatus === "idle" && invitesQuery.error === null;

  async function decline(invite: PartyInviteResponse) {
    // Invalidation changes cache state before React paints a disabled button.
    // Read that same UID-scoped authority again at the actual click boundary.
    const live = queryClient.getQueryState<PartyInviteResponse[]>([
      ...queryKeys.party.invitesMine(), actorId,
    ]);
    if (!sourceIsCurrent || live?.status !== "success" || live.fetchStatus !== "idle" ||
        live.isInvalidated || pending.current.has(invite.invite_id) ||
        !live.data?.some((row) => row.invite_id === invite.invite_id && row.facility_id === invite.facility_id)) {
      return;
    }
    pending.current.add(invite.invite_id);
    setPendingIds(new Set(pending.current));
    setDeclineError(null);
    try {
      await respond.mutateAsync({
        inviteId: invite.invite_id, facilityId: invite.facility_id, accept: false,
      });
    } catch (error) {
      if (mounted.current) setDeclineError(mapRespondPartyInviteError(error, false));
    } finally {
      pending.current.delete(invite.invite_id);
      if (mounted.current) setPendingIds(new Set(pending.current));
    }
  }

  return (
    <Page>
      <Typography component="h1" sx={{ fontSize: "1.375rem", fontWeight: 700 }}>
        Party invites
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        Decline here, or open an invitation to accept after checking in.
      </Typography>

      {invitesQuery.isLoading ? (
        <Stack
          direction="row"
          spacing={1}
          role="status"
          aria-live="polite"
          sx={{ alignItems: "center" }}
        >
          <CircularProgress size={20} />
          <Typography color="text.secondary">Loading your invites…</Typography>
        </Stack>
      ) : (
        <Stack spacing={2}>
          {invitesQuery.fetchStatus === "paused" ? (
            <Alert severity="info" role="status">
              Waiting for a connection to refresh invitations.
            </Alert>
          ) : null}
          {declineError ? <Alert severity="error" role="alert">{declineError}</Alert> : null}
          {invitesQuery.error !== null ? (
            <Alert severity="error" role="alert">
              <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                We couldn&apos;t load your party invitations.
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => void invitesQuery.refetch()}
                >
                  Retry
                </Button>
              </Stack>
            </Alert>
          ) : null}

          {invitesQuery.isSuccess && invitesQuery.error === null && invites.length === 0 ? (
            <Typography color="text.secondary">
              You have no pending party invites.
            </Typography>
          ) : null}
          {invites.length > 0 ? (
            <PartyInviteList
              invites={invites}
              mode="open-session"
              decliningInviteIds={pendingIds}
              declineDisabled={!sourceIsCurrent}
              onDecline={(invite) => { void decline(invite); }}
            />
          ) : null}
        </Stack>
      )}
    </Page>
  );
}
